/**
 * Preview del CTA que vería el visitante según /api/wishlist-config
 * (misma DB que /admin/wishlist-config · CTA por región).
 *
 * Mirror exacto de la cascada del Worker (worker.js):
 *   hide_global_hero_cta → global_hide
 *   city_ar (barrios) > province_ar (incl. CABA capital deferral) > country AR > DEFAULT
 *
 * CABA capital (cf.city=Buenos Aires / caba / …) defers to province_ar CABA so
 * “CABA → sin botón” is not overridden by stale city_ar community.
 */

import {
  isCabaCapitalCityArKey,
  isCabaProvinceGeoCode,
  normalizeCityKey,
  wishlistCityLookupKeys,
  type GeoCtaType,
} from "@/config/wishlistGeoCatalog";
import { findArCityEntry } from "./arLocations";

export type WishlistConfigEntry = {
  scope: "province_ar" | "country" | "city_ar" | string;
  geo_code: string;
  cta_type: GeoCtaType | string;
  enabled?: boolean;
  label?: string | null;
};

export type ResolvedWishlistCta = {
  cta_type: GeoCtaType | string;
  source:
    | "city_ar"
    | "province_ar"
    | "country_ar"
    | "country_default"
    | "global_hide"
    | "none";
  geo_code: string | null;
  label: string;
  /** geo_code city_ar canónico si hubo match de ciudad */
  cityGeoCode: string | null;
  provinceCode: string | null;
};

const CTA_LABEL: Record<string, string> = {
  none: "Sin botón",
  birthday: "Comunidad",
  community: "Exclusivo",
  wishlist: "Wishlist",
  dual_video: "Video Dual",
};

export function ctaTypeLabel(t: string): string {
  return CTA_LABEL[t] || t;
}

function enabled(e: WishlistConfigEntry): boolean {
  return e.enabled !== false;
}

function provinceArMatches(
  dbGeoCode: string,
  workerProvince: string | null,
): boolean {
  if (!workerProvince) return false;
  const a = normalizeCityKey(dbGeoCode);
  const b = normalizeCityKey(workerProvince);
  if (a === b) return true;
  return isCabaProvinceGeoCode(dbGeoCode) && isCabaProvinceGeoCode(workerProvince);
}

function findProvinceArRow(
  entries: WishlistConfigEntry[],
  province: string,
): WishlistConfigEntry | undefined {
  return entries.find(
    (e) =>
      e.scope === "province_ar" &&
      enabled(e) &&
      provinceArMatches(e.geo_code, province),
  );
}

/**
 * Resuelve CTA como el Worker de producción.
 * country ISO2, province = code admin (CABA | Buenos Aires | …), city = cf.city sim.
 *
 * @param opts.hideGlobalHeroCta — site_settings kill switch (anula geo).
 */
export function resolveWishlistCtaFromEntries(
  entries: WishlistConfigEntry[],
  opts: {
    country: string;
    province?: string | null;
    city?: string | null;
    hideGlobalHeroCta?: boolean;
  },
): ResolvedWishlistCta {
  const country = (opts.country || "").toUpperCase().slice(0, 2);
  const province = (opts.province || "").trim() || null;
  const city = (opts.city || "").trim() || null;
  const cityEntry = country === "AR" ? findArCityEntry(province, city) : null;

  if (opts.hideGlobalHeroCta) {
    return {
      cta_type: "none",
      source: "global_hide",
      geo_code: null,
      label: "Hero CTAs ocultos (global)",
      cityGeoCode: cityEntry?.geo_code ?? null,
      provinceCode: cityEntry?.provinceCode ?? province,
    };
  }

  // ── AR: city_ar with CABA capital deferral (Worker resolveArgentinaHeroCtaFromWishlist)
  if (country === "AR" && city) {
    const keys = wishlistCityLookupKeys(city);
    if (cityEntry) {
      keys.unshift(normalizeCityKey(cityEntry.geo_code));
    }
    const uniq = [...new Set(keys.filter(Boolean))];
    let matchedKey: string | null = null;
    let row: WishlistConfigEntry | undefined;
    for (const k of uniq) {
      const hit = entries.find(
        (e) =>
          e.scope === "city_ar" &&
          enabled(e) &&
          normalizeCityKey(e.geo_code) === k,
      );
      if (hit) {
        matchedKey = k;
        row = hit;
        break;
      }
    }

    if (row && matchedKey) {
      const deferToProvinceCaba =
        isCabaCapitalCityArKey(matchedKey) &&
        province &&
        isCabaProvinceGeoCode(province) &&
        entries.some(
          (e) =>
            e.scope === "province_ar" && isCabaProvinceGeoCode(e.geo_code),
        );

      if (!deferToProvinceCaba) {
        return {
          cta_type: row.cta_type,
          source: "city_ar",
          geo_code: row.geo_code,
          label: ctaTypeLabel(String(row.cta_type)),
          cityGeoCode: cityEntry?.geo_code ?? row.geo_code,
          provinceCode: cityEntry?.provinceCode ?? province,
        };
      }
      // fall through to province_ar CABA
    }
  }

  // Capital CF without province → Worker unknown-province path defers to CABA province row
  if (
    country === "AR" &&
    city &&
    !province &&
    isCabaCapitalCityArKey(normalizeCityKey(city))
  ) {
    const cabaRow = entries.find(
      (e) =>
        e.scope === "province_ar" &&
        enabled(e) &&
        isCabaProvinceGeoCode(e.geo_code),
    );
    if (cabaRow) {
      return {
        cta_type: cabaRow.cta_type,
        source: "province_ar",
        geo_code: cabaRow.geo_code,
        label: ctaTypeLabel(String(cabaRow.cta_type)),
        cityGeoCode: cityEntry?.geo_code ?? null,
        provinceCode: "CABA",
      };
    }
  }

  if (country === "AR" && province) {
    const row = findProvinceArRow(entries, province);
    if (row) {
      return {
        cta_type: row.cta_type,
        source: "province_ar",
        geo_code: row.geo_code,
        label: ctaTypeLabel(String(row.cta_type)),
        cityGeoCode: cityEntry?.geo_code ?? null,
        provinceCode: province,
      };
    }
  }

  if (country) {
    const row = entries.find(
      (e) =>
        e.scope === "country" &&
        enabled(e) &&
        String(e.geo_code).toUpperCase() === country,
    );
    if (row) {
      return {
        cta_type: row.cta_type,
        source: "country_ar",
        geo_code: row.geo_code,
        label: ctaTypeLabel(String(row.cta_type)),
        cityGeoCode: null,
        provinceCode: province,
      };
    }
    const def = entries.find(
      (e) =>
        e.scope === "country" &&
        enabled(e) &&
        String(e.geo_code).toUpperCase() === "DEFAULT",
    );
    if (def) {
      return {
        cta_type: def.cta_type,
        source: "country_default",
        geo_code: "DEFAULT",
        label: ctaTypeLabel(String(def.cta_type)),
        cityGeoCode: null,
        provinceCode: province,
      };
    }
  }

  return {
    cta_type: "none",
    source: "none",
    geo_code: null,
    label: "Sin fila en wishlist-config",
    cityGeoCode: cityEntry?.geo_code ?? null,
    provinceCode: province,
  };
}

export type WishlistConfigSnapshot = {
  entries: WishlistConfigEntry[];
  /**
   * Kill switch de /admin/wishlist-config.
   * Cuando es true el Worker fuerza hideCta + showCommunity=false
   * (ctaResolvedFrom=global_hide) y **anula** city_ar / province_ar / country.
   */
  hideGlobalHeroCta: boolean;
};

let cache: { at: number; snapshot: WishlistConfigSnapshot } | null = null;
const CACHE_MS = 15_000;

function emptySnapshot(): WishlistConfigSnapshot {
  return { entries: [], hideGlobalHeroCta: false };
}

/** Public GET — misma data que consume el Worker / admin (entries + kill switch). */
export async function fetchWishlistConfigSnapshot(
  force = false,
): Promise<WishlistConfigSnapshot> {
  if (!force && cache && Date.now() - cache.at < CACHE_MS) {
    return cache.snapshot;
  }
  try {
    const res = await fetch(`/api/wishlist-config?_t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return cache?.snapshot ?? emptySnapshot();
    const data = (await res.json()) as {
      entries?: WishlistConfigEntry[];
      hide_global_hero_cta?: boolean;
      hideGlobalHeroCta?: boolean;
    };
    const entries = Array.isArray(data.entries) ? data.entries : [];
    const hideGlobalHeroCta = Boolean(
      data.hide_global_hero_cta ?? data.hideGlobalHeroCta,
    );
    const snapshot: WishlistConfigSnapshot = { entries, hideGlobalHeroCta };
    cache = { at: Date.now(), snapshot };
    return snapshot;
  } catch {
    return cache?.snapshot ?? emptySnapshot();
  }
}

/** Public GET — entries only (compat). Prefer fetchWishlistConfigSnapshot. */
export async function fetchWishlistConfigEntries(
  force = false,
): Promise<WishlistConfigEntry[]> {
  const snap = await fetchWishlistConfigSnapshot(force);
  return snap.entries;
}
